import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * AboutWindowShowcase: 3D Perspective Double-Door Window (Screenshot 1 Codepen Mechanism)
 *
 * Sequence:
 * 1. Pinned dead-center on the screen with generous breathing room.
 * 2. Initially visible and strictly CLOSED with the circular Namaste emblem.
 * 3. On scrolling down, the window expands to container width, and double doors swing
 *    open in 3D perspective (-130deg / +130deg) with Codepen drop-shadows.
 * 4. Interior elements reveal in strict sequential order:
 *    - Avatar with scale & glow transition
 *    - First name only ("Nirajan")
 *    - "WEB ENGINEER" badge
 *    - 4 metric benchmark cards (cascading stagger)
 *    - "Learn More About Me" CTA button (without arrow icon)
 * 5. On scrolling down more:
 *    - Doors slowly swing closed back to 0deg, window contracts back, Namaste emblem returns.
 *    - "Still reading? Let's talk" CTA button reveals right under the Namaste emblem.
 * 6. Interactive tactile click-to-toggle at any time.
 */
export class AboutWindowShowcase {
  constructor() {
    this.section =
      document.getElementById('about-window-showcase-section') || document.getElementById('about');
    this.container = document.getElementById('about-window-pin-wrapper');
    this.windowEl = document.getElementById('about-window');
    this.doorLeft = this.windowEl?.querySelector('.nbk-window__door--left');
    this.doorRight = this.windowEl?.querySelector('.nbk-window__door--right');
    this.namasteSeal = this.windowEl?.querySelector('.nbk-window__namaste-seal');
    this.innerContent = this.windowEl?.querySelector('.nbk-window__content');
    this.avatarEl = this.windowEl?.querySelector('.nbk-window__avatar');
    this.nameEl = this.windowEl?.querySelector('.nbk-window__name');
    this.roleEl = this.windowEl?.querySelector('.nbk-window__role');
    this.descEl = this.windowEl?.querySelector('.nbk-window__desc');
    this.socialsEl = this.windowEl?.querySelector('.nbk-window__socials');
    this.ctaEl = this.windowEl?.querySelector('.nbk-window__cta');
    this.stillReadingBtn = document.getElementById('about-still-reading');

    if (!this.section || !this.container || !this.windowEl || !this.doorLeft || !this.doorRight) {
      return;
    }

    this.isOpen = false;
    this.init();
  }

  init() {
    this.initScrollChoreography();
    this.bindResizeObserver();
  }

  bindResizeObserver() {
    this.lastWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
    let resizeTimer = null;

    const handleResize = () => {
      if (typeof window === 'undefined') return;
      if (window.innerWidth === this.lastWidth) return;
      this.lastWidth = window.innerWidth;

      if (resizeTimer && typeof window !== 'undefined') window.clearTimeout(resizeTimer);
      if (typeof window !== 'undefined') {
        resizeTimer = window.setTimeout(() => {
          const trigger = ScrollTrigger.getById('about-window-scroll');
          if (trigger) {
            trigger.refresh();
          } else {
            ScrollTrigger.refresh();
          }
        }, 150);
      }
    };

    if (typeof window !== 'undefined' && window.ResizeObserver && this.section) {
      this.resizeObserver = new window.ResizeObserver(() => {
        handleResize();
      });
      this.resizeObserver.observe(this.section);
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize, { passive: true });
    }
  }

  getClosedWidth() {
    return '100%';
  }

  getOpenWidth() {
    return '100%';
  }

  initScrollChoreography() {
    // Initial states: Window has fixed container width (max-w-4xl) both closed and open
    gsap.set(this.doorLeft, {
      rotateY: 0,
      transformOrigin: '0% 50%',
      boxShadow: '0 4px 15px rgba(0,0,0,0.4)',
    });
    gsap.set(this.doorRight, {
      rotateY: 0,
      transformOrigin: '100% 50%',
      boxShadow: '0 4px 15px rgba(0,0,0,0.4)',
    });

    if (this.namasteSeal) {
      gsap.set(this.namasteSeal, { opacity: 1, scale: 1 });
    }
    if (this.stillReadingBtn) {
      gsap.set(this.stillReadingBtn, {
        opacity: 0,
        scale: 0.85,
        y: 15,
        pointerEvents: 'none',
      });
    }
    if (this.innerContent) {
      gsap.set(this.innerContent, { opacity: 0 });
    }
    if (this.avatarEl) {
      gsap.set(this.avatarEl, { opacity: 0, scale: 0.8, y: 20 });
    }
    if (this.nameEl) {
      gsap.set(this.nameEl, { opacity: 0, y: 15 });
    }
    if (this.roleEl) {
      gsap.set(this.roleEl, { opacity: 0, y: 15 });
    }
    if (this.descEl) {
      gsap.set(this.descEl, { opacity: 0, y: 15 });
    }
    if (this.socialsEl) {
      gsap.set(this.socialsEl, { opacity: 0, y: 12 });
    }
    if (this.ctaEl) {
      gsap.set(this.ctaEl, { opacity: 0, y: 12 });
    }

    // Pin the dedicated window section with smooth scrubbed physics
    this.tl = gsap.timeline({
      scrollTrigger: {
        id: 'about-window-scroll',
        trigger: this.section,
        start: 'top top',
        end: '+=2400',
        pin: true,
        pinSpacing: true,
        scrub: 1,
        anticipatePin: 1,
        onUpdate: (self) => {
          const p = self.progress;

          // Enable pointer interactions on the CTA button only when near completion
          if (this.stillReadingBtn) {
            this.stillReadingBtn.style.pointerEvents = p >= 0.9 ? 'auto' : 'none';
          }

          if (p > 0.08 && p < 0.84) {
            if (!this.isOpen) {
              this.isOpen = true;
              this.windowEl.classList.add('is-open');
            }
          } else {
            if (this.isOpen) {
              this.isOpen = false;
              this.windowEl.classList.remove('is-open');
            }
          }
        },
      },
    });

    // Step 0: Brief hold on closed state so user clearly sees the centered closed window
    this.tl.to({}, { duration: 0.5 });

    // Step 1: Smooth opening sequence
    // As the doors begin to swing open, the Namaste seal disappears immediately
    // so it NEVER lingers or displays inside the opening/opened chamber
    if (this.namasteSeal) {
      this.tl.to(
        this.namasteSeal,
        {
          opacity: 0,
          scale: 0.8,
          duration: 0.35,
          ease: 'power2.out',
        },
        '>',
      );
    }

    // Double doors swing open in true 3D (-130deg / +130deg)
    this.tl.to(
      this.doorLeft,
      {
        rotateY: -130,
        boxShadow: '15px 5px 30px 5px rgba(0,0,0,0.5)',
        duration: 2.0,
        ease: 'power1.inOut',
      },
      '<', // starts at the same instant as seal fade-out
    );

    this.tl.to(
      this.doorRight,
      {
        rotateY: 130,
        boxShadow: '-15px 5px 30px 5px rgba(0,0,0,0.5)',
        duration: 2.0,
        ease: 'power1.inOut',
      },
      '<',
    );

    // Step 2: Interior chamber reveals smoothly as doors pass halfway
    if (this.innerContent) {
      this.tl.to(
        this.innerContent,
        {
          opacity: 1,
          duration: 0.8,
          ease: 'power1.out',
        },
        '<1.0',
      );
    }

    // Step 3: Avatar pop transition
    if (this.avatarEl) {
      this.tl.to(
        this.avatarEl,
        {
          opacity: 1,
          scale: 1,
          y: 0,
          duration: 0.7,
          ease: 'back.out(1.3)',
        },
        '>-0.3',
      );
    }

    // Step 4: First name "Nirajan" displays AFTER avatar
    if (this.nameEl) {
      this.tl.to(
        this.nameEl,
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: 'power2.out',
        },
        '>-0.1',
      );
    }

    // Step 5: Web Engineer badge displays AFTER name
    if (this.roleEl) {
      this.tl.to(
        this.roleEl,
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: 'power2.out',
        },
        '>-0.1',
      );
    }

    // Step 6: Shorter bio description displays AFTER role
    if (this.descEl) {
      this.tl.to(
        this.descEl,
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: 'power2.out',
        },
        '>-0.1',
      );
    }

    // Step 7: Social media links display BELOW description
    if (this.socialsEl) {
      this.tl.to(
        this.socialsEl,
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          ease: 'power2.out',
        },
        '>-0.1',
      );
    }

    // Step 8: "Learn More About Me" CTA button displays AFTER socials
    if (this.ctaEl) {
      this.tl.to(
        this.ctaEl,
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          ease: 'power2.out',
        },
        '>-0.1',
      );
    }

    // Step 8: Plateau hold so user can comfortably browse and read everything inside
    this.tl.to({}, { duration: 2.2 });

    // Step 9: Smooth closing sequence
    // First, inner content fades away cleanly BEFORE doors begin their closing swing
    if (this.innerContent) {
      this.tl.to(
        this.innerContent,
        {
          opacity: 0,
          duration: 0.7,
          ease: 'power1.in',
        },
        '>',
      );
    }

    // Double doors smoothly swing closed back to 0deg (re-sealing)
    this.tl.to(
      this.doorLeft,
      {
        rotateY: 0,
        boxShadow: '0 4px 15px rgba(0,0,0,0.4)',
        duration: 2.0,
        ease: 'power1.inOut',
      },
      '>-0.1',
    );

    this.tl.to(
      this.doorRight,
      {
        rotateY: 0,
        boxShadow: '0 4px 15px rgba(0,0,0,0.4)',
        duration: 2.0,
        ease: 'power1.inOut',
      },
      '<',
    );

    // Step 10: ONLY AFTER doors are fully latched shut (at 0deg), the Namaste seal reappears!
    // Never displays before closing or while doors are in motion.
    if (this.namasteSeal) {
      this.tl.to(
        this.namasteSeal,
        {
          opacity: 1,
          scale: 1,
          duration: 0.45,
          ease: 'power2.out',
        },
        '>', // ONLY after door closing completes
      );
    }

    // Step 11: "Still reading? Let's talk" CTA button reveals under the Namaste seal
    if (this.stillReadingBtn) {
      this.tl.to(
        this.stillReadingBtn,
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.6,
          ease: 'power2.out',
        },
        '>-0.1',
      );
    }

    // Step 12: Hold on closed state before unpinning
    this.tl.to({}, { duration: 0.8 });

    // Refresh ScrollTrigger to calculate accurate pin-spacer heights
    requestAnimationFrame(() => {
      ScrollTrigger.refresh();
    });
  }
}
